import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { ProductCard } from "../components/ProductCard";
import { Search } from "lucide-react";

export default function Shop() {
  const { t, lang } = useI18n();
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const [q, setQ] = useState(params.get("q") || "");
  const [sort, setSort] = useState("featured");
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (category) p.category = category;
    if (q) p.q = q;
    api.get("/products", { params: p }).then(({ data }) => {
      setProducts(data);
      setLoading(false);
    });
  }, [category, q]);

  const sorted = useMemo(() => {
    const arr = [...products];
    if (sort === "priceLow") arr.sort((a, b) => a.price - b.price);
    else if (sort === "priceHigh") arr.sort((a, b) => b.price - a.price);
    else arr.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    return arr;
  }, [products, sort]);

  const setCat = (c) => {
    const next = new URLSearchParams(params);
    if (c) next.set("category", c); else next.delete("category");
    setParams(next);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="shop-page">
      <div className="mb-10">
        <div className="label-mono text-slate-500">{t("nav.shop")}</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">
          {category ? t(`categories.${category}`) : t("shop.title")}
        </h1>
      </div>

      <div className="grid md:grid-cols-[260px_1fr] gap-10">
        {/* SIDEBAR */}
        <aside className="space-y-6">
          <div className="border border-slate-200 p-4">
            <div className="label-mono text-slate-500 mb-3">{t("nav.categories")}</div>
            <div className="flex flex-col gap-1">
              <button
                onClick={() => setCat("")}
                className={`text-start px-3 py-2 text-sm font-medium ${!category ? "bg-[#8D1B3D] text-white" : "hover:bg-slate-100"}`}
                data-testid="filter-all"
              >
                {t("shop.all")}
              </button>
              {cats.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCat(c.id)}
                  className={`text-start px-3 py-2 text-sm font-medium ${category === c.id ? "bg-[#8D1B3D] text-white" : "hover:bg-slate-100"}`}
                  data-testid={`filter-${c.id}`}
                >
                  {lang === "ar" ? c.name_ar : c.name_en}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* MAIN */}
        <div>
          <div className="flex flex-wrap gap-3 items-center mb-6">
            <div className="flex-1 min-w-[240px] flex items-center border border-slate-300 px-3">
              <Search className="w-4 h-4 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("shop.search")}
                className="w-full px-3 py-2.5 outline-none text-sm bg-transparent"
                data-testid="search-input"
              />
            </div>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="border border-slate-300 px-3 py-2.5 text-sm font-medium outline-none"
              data-testid="sort-select"
            >
              <option value="featured">{t("shop.featured")}</option>
              <option value="priceLow">{t("shop.priceLow")}</option>
              <option value="priceHigh">{t("shop.priceHigh")}</option>
            </select>
          </div>

          {loading ? (
            <div className="text-slate-500 py-20 text-center">{t("common.loading")}</div>
          ) : sorted.length === 0 ? (
            <div className="border border-dashed border-slate-300 py-24 text-center text-slate-500" data-testid="shop-empty">
              {t("shop.empty")}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {sorted.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
